/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Card, Descriptions, Form, Space, Spin, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UpdateOauthClientRequest } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useUpdateOAuthClientMutation } from '@/hooks/mutation/useUpdateOAuthClientMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ClientFormActions } from '@/pages/OAuthClients/ClientFormActions';
import { ClientNameSection } from '@/pages/OAuthClients/ClientNameSection';
import { DeleteOAuthClientModal } from '@/pages/OAuthClients/DeleteOAuthClientModal';
import { RedirectUrisSection } from '@/pages/OAuthClients/RedirectUrisSection';
import { SignatureAlgSection } from '@/pages/OAuthClients/SignatureAlgSection';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

const { Title } = Typography;

export function OAuthClientDetailsPage() {
  const { t } = useMimicTranslation('oauthClients');
  const { oauthClientId } = useParams<{ oauthClientId: string }>();
  const [form] = Form.useForm<UpdateOauthClientRequest>();
  const navigate = useNavigate();
  const [message] = useMessage();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [isEditing, setIsEditing] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | undefined>();

  const { data: oauthClient, isLoading } = useQuery({
    queryKey: ['oauthClient', tenantID, oauthClientId],
    queryFn: async () => {
      return mgmtPlaneAPIClient.oauthClients.getOauthClient({
        tenantId: tenantID!,
        oauthClientId: oauthClientId!,
      });
    },
    enabled: !!tenantID && !!oauthClientId,
  });

  const { mutate: updateOAuthClient, isPending } = useUpdateOAuthClientMutation({
    onError: (error) => {
      message.error(t('updateError'));
      if (error instanceof Error) {
        // Handle validation errors - just show error message
        form.setFields([
          {
            name: 'name',
            errors: [error.message],
          },
        ]);
      }
    },
    onSuccess: () => {
      message.success(t('clientUpdated'));
      setIsEditing(false);
    },
  });

  const onFinish = (values: UpdateOauthClientRequest) => {
    if (!oauthClientId) return;

    updateOAuthClient({
      id: oauthClientId,
      name: values.name ? values.name.trim() : undefined,
      redirectUris: values.redirectUris ? values.redirectUris.map((uri) => uri.trim()) : undefined,
    });
  };

  const handleBack = () => {
    navigate(`/tenants/${tenantID}/oauth-clients`);
  };

  const handleDelete = () => {
    if (oauthClient?.data) {
      setClientToDelete({ id: oauthClient.data.id, name: oauthClient.data.name });
    }
  };

  if (!tenantID || !oauthClientId) return null;

  if (isLoading) {
    return (
      <ContentContainer width="100%">
        <Container full style={{ width: '100%', padding: '48px', textAlign: 'center' }}>
          <Spin size="large" />
        </Container>
      </ContentContainer>
    );
  }

  if (!oauthClient?.data) {
    return (
      <ContentContainer width="100%">
        <Container full style={{ width: '100%', padding: '48px' }}>
          <Text>{t('clientNotFound')}</Text>
        </Container>
      </ContentContainer>
    );
  }

  const client = oauthClient.data;

  return (
    <ContentContainer width="100%">
      <Container gap="lg" style={{ padding: '24px', width: '100%' }}>
        <Button type="text" onClick={handleBack} icon={<ArrowLeftOutlined />} style={{ alignSelf: 'flex-start' }}>
          {t('backToList')}
        </Button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <PageHeader title={client.name} level={2} />
          {!isEditing && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button type="default" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                {t('edit')}
              </Button>
              <Button type="primary" icon={<DeleteOutlined />} onClick={handleDelete}>
                {t('delete')}
              </Button>
            </div>
          )}
        </div>

        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ width: '100%' }}
            disabled={isPending}
            initialValues={{
              name: client.name,
              redirectUris: client.redirectUris,
              idTokenSignatureAlg: client.idTokenSignatureAlg,
            }}
          >
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <ClientNameSection />
              <RedirectUrisSection />
              <SignatureAlgSection readOnly />
              <ClientFormActions
                onCancel={() => setIsEditing(false)}
                isSubmitting={isPending}
                submitText={t('saveChanges')}
              />
            </Space>
          </Form>
        ) : (
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Card>
              <Container gap="md">
                <Title level={4} style={{ margin: 0 }}>
                  {t('detailsPage.basicInfo')}
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  contentStyle={{ padding: '8px 12px', fontSize: '13px' }}
                  labelStyle={{ padding: '8px 12px' }}
                >
                  <Descriptions.Item label={t('detailsPage.clientId')}>
                    <Text mono copyable>
                      {client.clientId}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.name')}>{client.name}</Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.redirectUris')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {client.redirectUris.map((uri) => (
                        <Text key={uri} mono copyable>
                          {uri}
                        </Text>
                      ))}
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Container>
            </Card>

            <Card>
              <Container gap="md">
                <Title level={4} style={{ margin: 0 }}>
                  {t('detailsPage.configuration')}
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  contentStyle={{ padding: '8px 12px', fontSize: '13px' }}
                  labelStyle={{ padding: '8px 12px' }}
                >
                  <Descriptions.Item label={t('detailsPage.confidential')}>
                    <Text>{client.confidential ? t('detailsPage.yes') : t('detailsPage.no')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.pkce')}>
                    <Text>{client.pkce ? t('detailsPage.enabled') : t('detailsPage.disabled')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.idTokenSignatureAlg')}>
                    <Text mono>{client.idTokenSignatureAlg}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.accessTokenTtl')}>
                    <Text>{client.accessTokenTtl}s</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.authorizationCodeTtl')}>
                    <Text>{client.authorizationCodeTtl}s</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.refreshTokenTtl')}>
                    <Text>{client.refreshTokenTtl}s</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detailsPage.supportedGrantTypes')}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {client.supportedGrantTypes.map((grantType) => (
                        <Text
                          key={grantType}
                          mono
                          style={{
                            background: '#f5f5f5',
                            padding: '2px 6px',
                            borderRadius: '3px',
                          }}
                        >
                          {grantType}
                        </Text>
                      ))}
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Container>
            </Card>
          </Space>
        )}
      </Container>

      {clientToDelete && (
        <DeleteOAuthClientModal
          oauthClientId={clientToDelete.id}
          oauthClientName={clientToDelete.name}
          onCancelDeletion={() => setClientToDelete(undefined)}
          onDeleteSuccess={() => navigate(`/tenants/${tenantID}/oauth-clients`)}
        />
      )}
    </ContentContainer>
  );
}
